import { Currency } from '@nemuru-eng/js-api-client';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { AutomobileFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/types/AutomobileFormItem';
import { DialogStepContent } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/components/DialogStepContent';
import { EditAutoPaymentMethodChangesReview } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/components/EditAutoPaymentMethodChangesReview';
import { EditFinancingStep } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/components/EditFinancingStep/EditFinancingStep';
import { EditStepWithLiveSummary } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/components/EditStepWithLiveSummary';
import { EditVehicleStep } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/components/EditVehicleStep';
import { useEditAutoPaymentMethodFlow } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/hooks/useEditAutoPaymentMethodFlow';
import { EDIT_AUTO_PAYMENT_METHOD_STEP_TITLE_TEXT_ID } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/maps/editAutoPaymentMethodStepTitle.map';
import { financingSummaryToFormFields } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/maps/editFinancingSimulator.map';
import { VehicleAmountRange } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/maps/vehicleAmountRange.map';
import { EditAutoPaymentMethodFormFields } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/schemas/editAutoPaymentMethodForm.schema';
import { EditAutoPaymentMethodDialogStep } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/EditAutoPaymentMethodDialogStep';
import { EditAutoPaymentMethodEntryMode } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/EditAutoPaymentMethodEntryMode';
import { FinancingLiveSummaryData } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/FinancingLiveSummaryData';

interface EditAutoPaymentMethodDialogFormProps {
  entryMode: EditAutoPaymentMethodEntryMode;
  checkoutSessionId: string;
  currency: Currency;
  initialFormItem: AutomobileFormItem;
  initialFinancing: FinancingLiveSummaryData;
  vehicleAmountRange: VehicleAmountRange;
  onClose: (_, reason: string | undefined) => void;
  onNext: () => void;
  goToStep: (step: EditAutoPaymentMethodDialogStep) => void;
  refreshCheckoutSessionAndPaymentMethodEntities: () => Promise<void>;
  step: EditAutoPaymentMethodDialogStep;
}

export const EditAutoPaymentMethodDialogForm = ({
  entryMode,
  step,
  onNext,
  goToStep,
  checkoutSessionId,
  currency,
  initialFormItem,
  initialFinancing,
  vehicleAmountRange,
  onClose,
  refreshCheckoutSessionAndPaymentMethodEntities,
}: EditAutoPaymentMethodDialogFormProps) => {
  const { setValue } = useFormContext<EditAutoPaymentMethodFormFields>();
  const { capabilities, confirmation, mutation, navigation, liveSummary } = useEditAutoPaymentMethodFlow({
    entryMode,
    step,
    checkoutSessionId,
    currency,
    initialFormItem,
    initialFinancing,
    onClose,
    onNext,
    goToStep,
    refreshCheckoutSessionAndPaymentMethodEntities,
  });
  const wasRequiringSimulatorRef = useRef(confirmation.requiresSimulatorFromVehicle);
  const selectedLenderName = capabilities.lenderRequestId ? capabilities.lenderName : undefined;

  useEffect(() => {
    if (step === EditAutoPaymentMethodDialogStep.SIMULATOR) {
      liveSummary.setShowLiveSummary(false);
      return;
    }

    if (step === EditAutoPaymentMethodDialogStep.VEHICLE) {
      liveSummary.setShowLiveSummary(true);
    }
  }, [step, liveSummary.setShowLiveSummary]);

  useEffect(() => {
    if (confirmation.needsFinancingReselection) {
      confirmation.clearSelectedPaymentMethodOptions();
    }
  }, [confirmation.needsFinancingReselection, confirmation.clearSelectedPaymentMethodOptions]);

  useEffect(() => {
    const wasRequiringSimulator = wasRequiringSimulatorRef.current;
    wasRequiringSimulatorRef.current = confirmation.requiresSimulatorFromVehicle;

    if (!confirmation.requiresSimulatorFromVehicle && wasRequiringSimulator) {
      confirmation.resetSimulatorSelectionToInitial();
      setValue('financing', financingSummaryToFormFields(initialFinancing));
    }
  }, [
    confirmation.requiresSimulatorFromVehicle,
    confirmation.resetSimulatorSelectionToInitial,
    initialFinancing,
    setValue,
  ]);

  useEffect(() => {
    if (mutation.showRequestDelayWarning && !mutation.loadingApply) {
      mutation.setShowRequestDelayWarning(false);
    }
  }, [mutation.loadingApply, mutation.showRequestDelayWarning, mutation.setShowRequestDelayWarning]);

  useEffect(() => {
    return () => mutation.clearDelayWarningTimeout();
  }, [mutation.clearDelayWarningTimeout]);

  if (!capabilities.paymentMethodId) {
    return null;
  }

  const stepTitleTextId = EDIT_AUTO_PAYMENT_METHOD_STEP_TITLE_TEXT_ID[step];

  return (
    <>
      {step === EditAutoPaymentMethodDialogStep.VEHICLE && (
        <DialogStepContent
          titleTextId={stepTitleTextId}
          prevButtonTextId={'CLOSE'}
          nextButtonTextId={'CONTINUE'}
          onNext={navigation.onVehicleNext}
          onPrev={() => onClose(null, undefined)}
          disabled={navigation.isNextDisabled}
        >
          <EditStepWithLiveSummary
            previewErrorMessage={mutation.previewError?.message}
            showLiveSummary={liveSummary.showLiveSummary}
            setShowLiveSummary={liveSummary.setShowLiveSummary}
            initialFormItem={initialFormItem}
            initialFinancing={initialFinancing}
            currency={currency}
            requiresFinancingReselection={confirmation.requiresSimulatorFromVehicle}
          >
            <EditVehicleStep
              currency={currency}
              editableFields={capabilities.editableFields}
              isFieldDisabled={capabilities.isFieldDisabled}
              initialFormItem={initialFormItem}
            />
          </EditStepWithLiveSummary>
        </DialogStepContent>
      )}

      {step === EditAutoPaymentMethodDialogStep.SIMULATOR && (
        <DialogStepContent
          titleTextId={stepTitleTextId}
          prevButtonTextId={entryMode === 'vehicle' ? 'BACK' : 'CLOSE'}
          nextButtonTextId={'CONTINUE'}
          onNext={() => void mutation.onPreview()}
          onPrev={navigation.onSimulatorPrev}
          loading={mutation.loadingPreview}
          disabled={navigation.isNextDisabled}
        >
          <EditStepWithLiveSummary
            previewErrorMessage={mutation.previewError?.message}
            showLiveSummary={liveSummary.showLiveSummary}
            setShowLiveSummary={liveSummary.setShowLiveSummary}
            initialFormItem={initialFormItem}
            initialFinancing={initialFinancing}
            currency={currency}
            showVehicleSummary={entryMode === 'vehicle'}
          >
            <EditFinancingStep
              currency={currency}
              editableFields={capabilities.editableFields}
              financingDefaults={initialFinancing}
              isEnabled={capabilities.isSimulatorEnabled}
              needsFinancingReselection={confirmation.needsFinancingReselection}
              showVehicleAmountControl={capabilities.showVehicleAmountControl}
              vehicleAmountRange={vehicleAmountRange}
              selectedLenderName={selectedLenderName}
              onFinancingConditionsSelected={confirmation.onFinancingConditionsSelected}
            />
          </EditStepWithLiveSummary>
        </DialogStepContent>
      )}

      {step === EditAutoPaymentMethodDialogStep.CHANGES_REVIEW &&
        mutation.reviewItem &&
        mutation.reviewFinancing &&
        mutation.modificationBody && (
          <EditAutoPaymentMethodChangesReview
            entryMode={entryMode}
            currency={currency}
            initialFormItem={initialFormItem}
            reviewItem={mutation.reviewItem}
            initialFinancing={initialFinancing}
            reviewFinancing={mutation.reviewFinancing}
            modificationBody={mutation.modificationBody}
            paymentMethodModificationPreview={mutation.paymentMethodModificationPreview}
            fallbackApr={capabilities.fallbackApr}
            selectedLenderName={selectedLenderName}
            onNext={() => void mutation.onApply()}
            onPrev={navigation.onReviewPrev}
            loading={mutation.loadingApply}
            showRequestDelayWarning={mutation.showRequestDelayWarning}
          />
        )}
    </>
  );
};
